'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { offlineDB, type OfflineVerification, type OfflineBoothInfo } from '@/lib/offline-db';
import { useOfflineSync } from '@/hooks/use-offline-sync';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  FileSpreadsheet,
  Printer,
  Loader2,
  ArrowLeft,
  Users,
  AlertCircle,
} from 'lucide-react';

export function Report18A() {
  const { token, user } = useAuthStore();
  const { setCurrentView, isOnline } = useAppStore();
  const { pendingCount } = useOfflineSync();

  const [boothInfo, setBoothInfo] = useState<OfflineBoothInfo | null>(null);
  const [verifications, setVerifications] = useState<OfflineVerification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reportDate, setReportDate] = useState('');

  const boothId = user?.boothId || '';

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await offlineDB.init();
        const info = await offlineDB.getBoothInfo(boothId);
        if (info) setBoothInfo(info);
        const localVerifications = await offlineDB.getVerifications(boothId);
        setVerifications(localVerifications);

        if (isOnline && token) {
          try {
            const res = await fetch(`/api/reports?boothId=${boothId}&type=18a`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              if (data.success && data.booth) {
                setBoothInfo((prev) => prev || {
                  pollingBoothId: data.booth.pollingBoothId,
                  name: data.booth.name,
                  address: data.booth.address || '',
                  district: data.booth.district || '',
                  constituency: data.booth.constituency || '',
                  constituencyNo: data.booth.constituencyNo || '',
                  wardNo: data.booth.wardNo || '',
                  totalVoters: data.booth.totalVoters || 0,
                  downloadedAt: new Date().toISOString(),
                });
              }
            }
          } catch { /* offline */ }
        }

        const now = new Date();
        setReportDate(now.toLocaleDateString('hi-IN', {
          day: '2-digit', month: 'long', year: 'numeric',
        }));
      } catch (error) {
        console.error('Failed to load report data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    if (boothId) loadData();
  }, [boothId, isOnline, token]);

  // Compute stats
  const totalVoters = boothInfo?.totalVoters || 0;
  const totalVerified = verifications.length;
  const tenderBallots = verifications.filter(v => v.verificationType === 'tender').length;
  const notAllowedToVote = 0;
  const decidedNotToVote = totalVoters > 0 ? Math.max(0, totalVoters - totalVerified - notAllowedToVote) : 0;
  const rejectedVotes = 0;
  const unusedBallots = totalVoters > totalVerified ? totalVoters - totalVerified : 0;

  const downloadReport = useCallback(() => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const boothName = boothInfo?.name || '';
    const boothIdVal = boothInfo?.pollingBoothId || '';
    const wardNo = boothInfo?.wardNo || '';

    printWindow.document.write(`<!DOCTYPE html>
<html lang="hi">
<head>
  <meta charset="UTF-8" />
  <title>फॉर्म-18अ | Form 18-A</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap');

    * { margin: 0; padding: 0; box-sizing: border-box; }

    body {
      font-family: 'Noto Sans Devanagari', Arial, sans-serif;
      padding: 24px 32px;
      color: #000;
      font-size: 12px;
      line-height: 1.6;
    }

    .form-container {
      max-width: 780px;
      margin: 0 auto;
    }

    /* ===== HEADER ===== */
    .header {
      text-align: center;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 3px double #000;
    }
    .annexure {
      font-size: 14px;
      font-weight: 600;
      margin-bottom: 4px;
    }
    .form-title {
      font-size: 20px;
      font-weight: 700;
      margin-bottom: 3px;
    }
    .legal-ref {
      font-size: 12px;
      color: #333;
      margin-bottom: 8px;
    }
    .part-title {
      font-size: 15px;
      font-weight: 700;
      margin-bottom: 2px;
    }
    .part-subtitle {
      font-size: 12px;
      color: #333;
      margin-bottom: 12px;
    }

    /* ===== BODY INFO ===== */
    .body-info {
      text-align: center;
      font-size: 12px;
      line-height: 2.2;
      margin-bottom: 12px;
    }
    .body-info .blank {
      display: inline-block;
      min-width: 180px;
      border-bottom: 1px solid #000;
      margin: 0 4px;
      font-weight: 500;
    }

    /* ===== POLLING STATION INFO ===== */
    .polling-info {
      margin-bottom: 14px;
      font-size: 12px;
      line-height: 2;
    }
    .polling-info .info-row {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      gap: 4px;
      margin: 4px 0;
    }
    .polling-info .blank {
      display: inline-block;
      min-width: 180px;
      border-bottom: 1px solid #000;
    }
    .polling-info .blank-sm {
      display: inline-block;
      min-width: 100px;
      border-bottom: 1px solid #000;
    }
    .polling-info .indent {
      margin-left: 28px;
    }
    .polling-info .indent-lg {
      margin-left: 48px;
    }

    /* ===== NUMBERED SECTIONS ===== */
    .numbered-section {
      margin-bottom: 14px;
      padding-top: 10px;
      border-top: 1px solid #000;
    }
    .numbered-item {
      display: flex;
      align-items: baseline;
      font-size: 12px;
      margin: 6px 0;
      line-height: 1.8;
    }
    .numbered-item .num {
      font-weight: 700;
      min-width: 22px;
    }
    .numbered-item .text {
      flex: 1;
    }
    .numbered-item .value {
      font-weight: 700;
      min-width: 40px;
      text-align: right;
      border-bottom: 1px solid #000;
      padding: 0 6px;
    }

    /* ===== SERIAL NUMBER TABLE ===== */
    .serial-section {
      margin-bottom: 14px;
      padding-top: 10px;
      border-top: 1px solid #000;
    }
    .serial-section .section-title {
      font-size: 13px;
      font-weight: 700;
      margin-bottom: 8px;
    }
    .serial-table {
      width: 100%;
      border-collapse: collapse;
    }
    .serial-table th,
    .serial-table td {
      border: 1px solid #000;
      padding: 5px 8px;
      font-size: 12px;
      text-align: center;
      vertical-align: middle;
    }
    .serial-table th {
      background: #f5f5f5;
      font-weight: 600;
    }
    .serial-table td.left {
      text-align: left;
    }
    .serial-table td.value {
      font-weight: 600;
    }

    /* ===== BALLOT SECTION ===== */
    .ballot-section {
      margin-bottom: 14px;
      padding-top: 10px;
      border-top: 1px solid #000;
    }
    .ballot-section .section-title {
      font-size: 13px;
      font-weight: 700;
      margin-bottom: 8px;
    }
    .ballot-item {
      display: flex;
      align-items: baseline;
      font-size: 12px;
      margin: 5px 0;
      line-height: 1.8;
      gap: 4px;
    }
    .ballot-item .num {
      font-weight: 700;
      min-width: 22px;
    }
    .ballot-item .blank-val {
      display: inline-block;
      min-width: 50px;
      border-bottom: 1px solid #000;
      text-align: center;
      font-weight: 700;
    }
    .ballot-item .blank-lg {
      display: inline-block;
      min-width: 120px;
      border-bottom: 1px solid #000;
    }
    .ballot-item .indent {
      margin-left: 26px;
    }

    /* ===== FOOTER ===== */
    .footer-section {
      margin-top: 32px;
      padding-top: 12px;
    }
    .footer-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    .footer-left {
      font-size: 12px;
    }
    .footer-left .field-row {
      margin: 6px 0;
      display: flex;
      align-items: baseline;
      gap: 4px;
    }
    .footer-left .blank {
      display: inline-block;
      min-width: 140px;
      border-bottom: 1px solid #000;
    }
    .footer-right {
      text-align: center;
      font-size: 12px;
    }
    .sign-line {
      border-top: 1px solid #000;
      width: 200px;
      padding-top: 4px;
      margin-top: 40px;
      text-align: center;
      font-weight: 500;
    }
    .sign-line + .sign-line {
      margin-top: 28px;
    }

    /* ===== PRINT ===== */
    @media print {
      body { padding: 12px 16px; }
      .form-container { max-width: 100%; }
    }
  </style>
</head>
<body>
  <div class="form-container">

    <!-- ===== HEADER ===== -->
    <div class="header">
      <p class="annexure">परिशिष्ट-पाँच</p>
      <h1 class="form-title">फॉर्म-18अ</h1>
      <p class="legal-ref">(नियम 63एफ के अनुसार)</p>
      <p class="part-title">भाग 1 — विहित मतों का लेखा</p>
      <p class="part-subtitle">(जहाँ ई.वी.एम. से मतदान किया जाएगा)</p>

      <div class="body-info">
        <p>
          नगर निगम/नगर पालिका परिषद/नगर परिषद
          <span class="blank">${boothName}</span>
          के
        </p>
        <p>
          अध्यक्ष/अध्यक्ष का चुनाव तथा वार्ड क्रमांक
          <span class="blank">${wardNo}</span>
          से व्यक्ति का चुनाव
        </p>
      </div>
    </div>

    <!-- ===== POLLING STATION INFO ===== -->
    <div class="polling-info">
      <div class="info-row">
        <span>मतदान केंद्र क्रमांक और नाम</span>
        <span class="blank">${boothName}</span>
      </div>
      <div class="info-row">
        <span>नियंत्रण स्टेशन क्र.</span>
        <span class="blank-sm">${boothIdVal}</span>
      </div>
      <div class="info-row">
        <span>मतदान केंद्र में प्रयुक्त मतदान मशीन का पहचान निर्वाचक</span>
      </div>
      <div class="info-row indent">
        <span>1.</span>
        <span class="blank"></span>
        <span>मतदान स्टेशन क्र.</span>
        <span class="blank-sm">1</span>
      </div>
      <div class="info-row indent">
        <span>2.</span>
        <span class="blank"></span>
      </div>
      <div class="info-row">
        <span>प्रिंटर क्र. (यदि प्रयुक्त हुआ हो)</span>
        <span class="blank"></span>
      </div>
      <div class="info-row">
        <span>एस.ई.एम. का क्रमांक</span>
        <span class="blank"></span>
      </div>
    </div>

    <!-- ===== NUMBERED SECTIONS 1–6 ===== -->
    <div class="numbered-section">
      <div class="numbered-item">
        <span class="num">1.</span>
        <span class="text">मतदान केंद्र को निर्धारित मतदाताओं की कुल संख्या</span>
        <span class="value">${totalVoters}</span>
      </div>
      <div class="numbered-item">
        <span class="num">2.</span>
        <span class="text">मतदाता रजिस्टर में अंकित मतदाताओं की कुल संख्या</span>
        <span class="value">${totalVoters}</span>
      </div>
      <div class="numbered-item">
        <span class="num">3.</span>
        <span class="text">मतदान मशीन के अनुसार विहित वोटों की संख्या</span>
        <span class="value">${totalVerified}</span>
      </div>
      <div class="numbered-item">
        <span class="num">4.</span>
        <span class="text">नियम 63बी के अधीन मतदान करने के लिए अनुमति न दी गई मतदाताओं की संख्या</span>
        <span class="value">${notAllowedToVote}</span>
      </div>
      <div class="numbered-item">
        <span class="num">5.</span>
        <span class="text">मतदान न करने का निर्णय करने वाले मतदाताओं की संख्या (2−3−4 = 5)</span>
        <span class="value">${decidedNotToVote}</span>
      </div>
      <div class="numbered-item">
        <span class="num">6.</span>
        <span class="text">प्रत्याख्यात मतों की संख्या</span>
        <span class="value">${rejectedVotes}</span>
      </div>
    </div>

    <!-- ===== SERIAL NUMBER TABLE ===== -->
    <div class="serial-section">
      <p class="section-title">क्रम संख्या</p>
      <table class="serial-table">
        <thead>
          <tr>
            <th style="width:55%">विवरण</th>
            <th style="width:22.5%">कुल (से)</th>
            <th style="width:22.5%">कुल (तक)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="left">(अ) उपयोग के लिए प्राप्त</td>
            <td class="value">1</td>
            <td class="value">${totalVoters}</td>
          </tr>
          <tr>
            <td class="left">(ब) मतदाताओं को जारी किए गए</td>
            <td class="value">1</td>
            <td class="value">${totalVerified}</td>
          </tr>
          <tr>
            <td class="left">(ग) प्रयुक्त न किए गए और वापस लिए गए</td>
            <td class="value"></td>
            <td class="value">${unusedBallots}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ===== SECTION 7: BALLOT PAPER ACCOUNT ===== -->
    <div class="ballot-section">
      <p class="section-title"><strong>7.</strong> बैलेट पत्रों का लेखा</p>

      <div class="ballot-item">
        <span class="num">1.</span>
        <span>उपयोग के लिए प्राप्य की गई कुल सं.</span>
        <span class="blank-val">${totalVoters}</span>
      </div>
      <div class="ballot-item indent">
        <span>(बैलेट पत्री— क्र.सं.</span>
        <span class="blank-val">1</span>
        <span>से</span>
        <span class="blank-val">${totalVoters}</span>
        <span>तक)</span>
      </div>

      <div class="ballot-item">
        <span class="num">2.</span>
        <span>प्रयुक्त हुई बैलेट पत्रियों की कुल सं.</span>
        <span class="blank-val">${totalVerified}</span>
      </div>
      <div class="ballot-item indent">
        <span>(क्र.सं.</span>
        <span class="blank-val">1</span>
        <span>से</span>
        <span class="blank-val">${totalVerified}</span>
        <span>तक)</span>
      </div>

      <div class="ballot-item">
        <span class="num">3.</span>
        <span>टेंडर्ड/फॉर्म ली वापस की गई अप्रयुक्त बैलेट पत्रियों की कुल सं.</span>
        <span class="blank-val">${tenderBallots}</span>
      </div>
      <div class="ballot-item indent">
        <span>(क्र.सं.</span>
        <span class="blank-val"></span>
        <span>से</span>
        <span class="blank-val"></span>
        <span>तक)</span>
      </div>

      <div class="ballot-item">
        <span class="num">4.</span>
        <span>नष्ट हुई बैलेट पत्रियों की कुल सं.</span>
        <span class="blank-val">0</span>
      </div>
      <div class="ballot-item indent">
        <span>(यदि कोई हों, क्र.सं.</span>
        <span class="blank-val"></span>
        <span>से</span>
        <span class="blank-val"></span>
        <span>तक)</span>
      </div>
    </div>

    <!-- ===== FOOTER ===== -->
    <div class="footer-section">
      <div class="footer-row">
        <div class="footer-left">
          <div class="field-row">
            <span>दिनांक:</span>
            <span class="blank">${reportDate}</span>
          </div>
          <div class="field-row">
            <span>स्थान:</span>
            <span class="blank">${boothName}</span>
          </div>
        </div>
        <div class="footer-right">
          <div class="sign-line">मतदान अधिकारी के हस्ताक्षर</div>
          <div class="sign-line">पीठासीन अधिकारी के हस्ताक्षर</div>
        </div>
      </div>
    </div>

  </div>
</body>
</html>`);

    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  }, [boothInfo, reportDate, totalVoters, totalVerified, tenderBallots, notAllowedToVote, decidedNotToVote, rejectedVotes, unusedBallots]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-3">
          <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto" />
          <p className="text-emerald-800 font-medium">रिपोर्ट लोड हो रही है...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">
      {/* Header Bar - Outside printable content */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-6 h-6 text-emerald-700" />
          <div>
            <h2 className="text-lg font-bold text-emerald-900">फॉर्म-18अ</h2>
            <p className="text-xs text-gray-500">विहित मतों का लेखा | Account of Valid Votes</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">
            <Users className="w-3 h-3 mr-1" />
            {totalVerified} सत्यापित
          </Badge>
          {pendingCount > 0 && (
            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
              {pendingCount} लंबित सिंक
            </Badge>
          )}
          <Button onClick={downloadReport} className="bg-emerald-600 hover:bg-emerald-700 text-white min-h-[44px]">
            <Printer className="w-4 h-4 mr-2" />
            प्रिंट / डाउनलोड
          </Button>
        </div>
      </div>

      {/* Report Content - Card-based on-screen view */}
      <Card className="border-emerald-200 shadow-md" id="report-18a-content">
        <CardContent className="p-6">

          {/* ===== HEADER SECTION ===== */}
          <div className="text-center mb-5 pb-4 border-b-[3px] border-double border-black">
            <p className="text-sm font-semibold text-gray-800 mb-1">परिशिष्ट-पाँच</p>
            <h1 className="text-xl font-bold text-black mb-1">फॉर्म-18अ</h1>
            <p className="text-xs text-gray-700 mb-2">(नियम 63एफ के अनुसार)</p>
            <p className="text-sm font-bold text-black mb-1">भाग 1 — विहित मतों का लेखा</p>
            <p className="text-xs text-gray-600 mb-4">(जहाँ ई.वी.एम. से मतदान किया जाएगा)</p>

            {/* Body info */}
            <div className="text-xs leading-8">
              <p>
                नगर निगम/नगर पालिका परिषद/नगर परिषद{' '}
                <span className="inline-block min-w-[180px] border-b border-black mx-1 font-medium">{boothInfo?.name || ''}</span>
                {' '}के
              </p>
              <p>
                अध्यक्ष/अध्यक्ष का चुनाव तथा वार्ड क्रमांक{' '}
                <span className="inline-block min-w-[120px] border-b border-black mx-1 font-medium">{boothInfo?.wardNo || ''}</span>
                {' '}से व्यक्ति का चुनाव
              </p>
            </div>
          </div>

          {/* ===== POLLING STATION INFO ===== */}
          <div className="space-y-1.5 text-xs mb-5">
            <div className="flex items-baseline gap-1 flex-wrap">
              <span className="shrink-0">मतदान केंद्र क्रमांक और नाम</span>
              <span className="inline-block min-w-[200px] border-b border-black">{boothInfo?.name || ''}</span>
            </div>
            <div className="flex items-baseline gap-1 flex-wrap">
              <span className="shrink-0">नियंत्रण स्टेशन क्र.</span>
              <span className="inline-block min-w-[100px] border-b border-black">{boothInfo?.pollingBoothId || ''}</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span>मतदान केंद्र में प्रयुक्त मतदान मशीन का पहचान निर्वाचक</span>
            </div>
            <div className="flex items-baseline gap-2 ml-7 flex-wrap">
              <span className="font-semibold">1.</span>
              <span className="inline-block min-w-[200px] border-b border-black"></span>
              <span>मतदान स्टेशन क्र.</span>
              <span className="inline-block min-w-[100px] border-b border-black">1</span>
            </div>
            <div className="flex items-baseline gap-2 ml-7">
              <span className="font-semibold">2.</span>
              <span className="inline-block min-w-[200px] border-b border-black"></span>
            </div>
            <div className="flex items-baseline gap-1 flex-wrap">
              <span className="shrink-0">प्रिंटर क्र. (यदि प्रयुक्त हुआ हो)</span>
              <span className="inline-block min-w-[200px] border-b border-black"></span>
            </div>
            <div className="flex items-baseline gap-1 flex-wrap">
              <span className="shrink-0">एस.ई.एम. का क्रमांक</span>
              <span className="inline-block min-w-[200px] border-b border-black"></span>
            </div>
          </div>

          {/* ===== NUMBERED SECTIONS 1-6 ===== */}
          <div className="space-y-2 text-xs mb-5 border-t border-black pt-4">
            <div className="flex items-baseline gap-2">
              <span className="font-bold min-w-[20px]">1.</span>
              <span className="flex-1">मतदान केंद्र को निर्धारित मतदाताओं की कुल संख्या</span>
              <span className="font-bold text-sm border-b border-black min-w-[40px] text-center">{totalVoters}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-bold min-w-[20px]">2.</span>
              <span className="flex-1">मतदाता रजिस्टर में अंकित मतदाताओं की कुल संख्या</span>
              <span className="font-bold text-sm border-b border-black min-w-[40px] text-center">{totalVoters}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-bold min-w-[20px]">3.</span>
              <span className="flex-1">मतदान मशीन के अनुसार विहित वोटों की संख्या</span>
              <span className="font-bold text-sm border-b border-black min-w-[40px] text-center">{totalVerified}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-bold min-w-[20px]">4.</span>
              <span className="flex-1">नियम 63बी के अधीन मतदान करने के लिए अनुमति न दी गई मतदाताओं की संख्या</span>
              <span className="font-bold text-sm border-b border-black min-w-[40px] text-center">{notAllowedToVote}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-bold min-w-[20px]">5.</span>
              <span className="flex-1">मतदान न करने का निर्णय करने वाले मतदाताओं की संख्या (2−3−4 = 5)</span>
              <span className="font-bold text-sm border-b border-black min-w-[40px] text-center">{decidedNotToVote}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-bold min-w-[20px]">6.</span>
              <span className="flex-1">प्रत्याख्यात मतों की संख्या</span>
              <span className="font-bold text-sm border-b border-black min-w-[40px] text-center">{rejectedVotes}</span>
            </div>
          </div>

          {/* ===== SERIAL NUMBER TABLE ===== */}
          <div className="mb-5 border-t border-black pt-4">
            <p className="text-xs font-bold mb-3">क्रम संख्या</p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-black px-4 py-2 text-left font-semibold w-[55%]">विवरण</th>
                    <th className="border border-black px-4 py-2 text-center font-semibold w-[22.5%]">कुल (से)</th>
                    <th className="border border-black px-4 py-2 text-center font-semibold w-[22.5%]">कुल (तक)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-black px-4 py-2">(अ) उपयोग के लिए प्राप्त</td>
                    <td className="border border-black px-4 py-2 text-center font-bold">1</td>
                    <td className="border border-black px-4 py-2 text-center font-bold">{totalVoters}</td>
                  </tr>
                  <tr>
                    <td className="border border-black px-4 py-2">(ब) मतदाताओं को जारी किए गए</td>
                    <td className="border border-black px-4 py-2 text-center font-bold">1</td>
                    <td className="border border-black px-4 py-2 text-center font-bold">{totalVerified}</td>
                  </tr>
                  <tr>
                    <td className="border border-black px-4 py-2">(ग) प्रयुक्त न किए गए और वापस लिए गए</td>
                    <td className="border border-black px-4 py-2 text-center font-bold"></td>
                    <td className="border border-black px-4 py-2 text-center font-bold">{unusedBallots}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ===== SECTION 7: BALLOT PAPER ACCOUNT ===== */}
          <div className="mb-5 border-t border-black pt-4">
            <p className="text-xs font-bold mb-3">
              <span className="font-bold">7.</span>{' '}
              बैलेट पत्रों का लेखा
            </p>

            <div className="space-y-2 text-xs">
              {/* 7.1 */}
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-bold min-w-[20px]">1.</span>
                <span>उपयोग के लिए प्राप्य की गई कुल सं.</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center font-bold ml-2">{totalVoters}</span>
              </div>
              <div className="flex items-baseline gap-1 ml-7 flex-wrap">
                <span>(बैलेट पत्री— क्र.सं.</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center">1</span>
                <span>से</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center">{totalVoters}</span>
                <span>तक)</span>
              </div>

              {/* 7.2 */}
              <div className="flex items-baseline gap-2 flex-wrap mt-2">
                <span className="font-bold min-w-[20px]">2.</span>
                <span>प्रयुक्त हुई बैलेट पत्रियों की कुल सं.</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center font-bold ml-2">{totalVerified}</span>
              </div>
              <div className="flex items-baseline gap-1 ml-7 flex-wrap">
                <span>(क्र.सं.</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center">1</span>
                <span>से</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center">{totalVerified}</span>
                <span>तक)</span>
              </div>

              {/* 7.3 */}
              <div className="flex items-baseline gap-2 flex-wrap mt-2">
                <span className="font-bold min-w-[20px]">3.</span>
                <span>टेंडर्ड/फॉर्म ली वापस की गई अप्रयुक्त बैलेट पत्रियों की कुल सं.</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center font-bold ml-2">{tenderBallots}</span>
              </div>
              <div className="flex items-baseline gap-1 ml-7 flex-wrap">
                <span>(क्र.सं.</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center"></span>
                <span>से</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center"></span>
                <span>तक)</span>
              </div>

              {/* 7.4 */}
              <div className="flex items-baseline gap-2 flex-wrap mt-2">
                <span className="font-bold min-w-[20px]">4.</span>
                <span>नष्ट हुई बैलेट पत्रियों की कुल सं.</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center font-bold ml-2">0</span>
              </div>
              <div className="flex items-baseline gap-1 ml-7 flex-wrap">
                <span>(यदि कोई हों, क्र.सं.</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center"></span>
                <span>से</span>
                <span className="inline-block min-w-[50px] border-b border-black text-center"></span>
                <span>तक)</span>
              </div>
            </div>
          </div>

          {/* ===== FOOTER ===== */}
          <div className="mt-8 pt-4 border-t border-black">
            <div className="flex items-start justify-between">
              <div className="text-xs space-y-3">
                <div className="flex items-baseline gap-2">
                  <span>दिनांक:</span>
                  <span className="inline-block min-w-[140px] border-b border-black">{reportDate}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span>स्थान:</span>
                  <span className="inline-block min-w-[140px] border-b border-black">{boothInfo?.name || ''}</span>
                </div>
              </div>
              <div className="text-center text-xs space-y-0">
                <div className="w-52 border-t border-black pt-2 mt-10">
                  <p className="font-medium">मतदान अधिकारी के हस्ताक्षर</p>
                </div>
                <div className="w-52 border-t border-black pt-2 mt-8">
                  <p className="font-medium">पीठासीन अधिकारी के हस्ताक्षर</p>
                </div>
              </div>
            </div>
          </div>

          {/* Booth ID reference at bottom */}
          <div className="mt-4 text-xs text-gray-500">
            मतदान केंद्र क्र. {boothInfo?.pollingBoothId || '___'}
          </div>
        </CardContent>
      </Card>

      {/* Back Button - Outside printable content */}
      <div className="flex justify-center pt-2 pb-4">
        <Button variant="outline" onClick={() => setCurrentView('officer-dashboard')} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 min-h-[44px]">
          <ArrowLeft className="w-4 h-4 mr-2" />
          वापस जाएं
        </Button>
      </div>
    </div>
  );
}
